"use client"

import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"

import { fetchLookup } from "@/shared/lib/api/lookups-api"
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value"
import { type TenantScopeContext } from "@/shared/model/tenant-scope-context"
import { type LookupQueryParams, type LookupResponse, type LookupType } from "@/shared/model/lookup-types"

export type UseLookupParams = {
  type: LookupType
  query?: string
  limit?: number
  enabled?: boolean
  debounceMs?: number
  workspaceId?: string
  branchId?: string | null
  context?: TenantScopeContext
  // Lookups return only selectable records by default. Set true ONLY for table filters that must match
  // historical/inactive records — never for "pick a valid record" pickers.
  includeInactive?: boolean
}

const DEFAULT_LIMIT = 10

export const lookupQueryKeys = {
  all: ["lookups"] as const,
  list: (params: {
    type: LookupType
    query: string
    limit: number
    workspaceId?: string
    branchId?: string | null
    context?: TenantScopeContext
    includeInactive?: boolean
  }) =>
    [
      ...lookupQueryKeys.all,
      params.type,
      "list",
      {
        query: params.query || "__all__",
        limit: params.limit,
        workspaceId: params.workspaceId ?? "__workspace__",
        branchId: typeof params.branchId === "undefined" ? "__branch__" : params.branchId,
        context: params.context ?? "__context__",
        includeInactive: params.includeInactive ?? "__includeInactive__",
      },
    ] as const,
}

export function buildLookupQueryParams(params: UseLookupParams): LookupQueryParams {
  const trimmedQuery = typeof params.query === "string" ? params.query.trim() : ""
  const rawLimit = typeof params.limit === "number" ? params.limit : DEFAULT_LIMIT
  const limit = Math.min(Math.max(rawLimit, 1), 50)

  return {
    q: trimmedQuery || undefined,
    limit,
    workspaceId: params.workspaceId,
    branchId: params.branchId,
    context: params.context,
    includeInactive: params.includeInactive,
  }
}

export function useLookup(params: UseLookupParams) {
  const queryParams = useMemo(() => buildLookupQueryParams(params), [params])
  const debouncedQuery = useDebouncedValue(queryParams.q ?? "", params.debounceMs ?? 300)
  const limit = queryParams.limit ?? DEFAULT_LIMIT
  const enabled = params.enabled ?? true

  return useQuery<LookupResponse>({
    queryKey: lookupQueryKeys.list({
      type: params.type,
      query: debouncedQuery,
      limit,
      workspaceId: params.workspaceId,
      branchId: params.branchId,
      context: params.context,
      includeInactive: params.includeInactive,
    }),
    queryFn: () =>
      fetchLookup(params.type, {
        q: debouncedQuery || undefined,
        limit,
        workspaceId: queryParams.workspaceId,
        branchId: queryParams.branchId,
        context: queryParams.context,
        includeInactive: queryParams.includeInactive,
      }),
    enabled,
    staleTime: 60_000,
  })
}
